import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../../components/header/Header';
import LoaderComponent from '../../../components/Loader.component';
import TagsModal from '../../../components/tags/TagsModal';
import styled from 'styled-components';
import { MONITOR_TYPES } from '../../../../constants/monitor.types';
import CreateNewServerComponent from './CreateNewServerComponent';
import CreateNewMonitorComponent from './CreateNewMonitorComponent';
import { EuiIcon } from '@elastic/eui';
import CreateNewMongoMonitorComponent from './CreateNewMongoMonitorComponent';
import serverStore from '../../../../stores/server.store';
import tagsStore from '../../../../stores/tags.store';
import uptimeStore from '../../../../stores/uptime.store';
import mongoDbStore from '../../../../stores/mongodb.store';
import { addToast } from '../../../components/toasts/Toast';
import {
    canShowMonitorForm,
    selectAvailableMonitorType
} from './monitorAvailability';

const Styles = styled.main`
  .done {
    margin-top: 24px;
  }
`;

const ChooseMonitorStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-gap: 12px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const ChooseMonitorWrapStyled = styled.div`
  width: min(1120px, calc(100% - 28px));
  margin: 14px auto 30px;
  padding: 18px;
  border-radius: 8px;
  background-color: rgb(29,30,36);
  box-shadow: rgb(0 0 0 / 28%) 0px 0.9px 4px, rgb(0 0 0 / 21%) 0px 2.6px 8px, rgb(0 0 0 / 18%) 0px 5.7px 12px, rgb(0 0 0 / 14%) 0px 15px 15px;

  @media (max-width: 600px) {
    width: 100%;
    padding: 12px;
  }

  h1 {
    font-size: 30px;
    line-height: 1.15;
    margin: 0;

    @media (max-width: 600px) {
      font-size: 24px;
    }
  }
`;

const ChooseMonitorItemStyled = styled.div`
  background: ${ props => props.active ? 'rgba(0, 128, 213, 0.12)' : 'linear-gradient(135deg, rgb(35, 37, 47) 0%, rgb(24, 25, 30) 100%)' };
  border: 1px solid ${ props => props.active ? 'rgba(0, 128, 213, 0.42)' : 'rgba(255, 255, 255, 0.06)' };
  border-radius: 8px;
  cursor: pointer;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  grid-gap: 11px;
  min-height: 104px;
  padding: 14px;
  position: relative;
  transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;

  &:hover {
    border-color: rgba(0, 128, 213, 0.28);
    transform: translateY(-1px);
  }

  .icon-wrapper {
    align-items: center;
    background: rgba(0, 128, 213, 0.12);
    border: 1px solid rgba(0, 128, 213, 0.25);
    border-radius: 6px;
    color: #36A2EF;
    display: flex;
    height: 34px;
    justify-content: center;
    width: 34px;
  }

  .title {
    color: rgba(255, 255, 255, 0.92);
    display: block;
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 5px;
  }

  .description {
    color: rgba(255, 255, 255, 0.55);
    display: block;
    font-size: 12px;
    line-height: 1.4;
  }

  .status {
    color: ${ props => props.disabled ? '#F86B63' : '#50bc5e' };
    display: block;
    font-size: 11px;
    font-weight: 700;
    margin-top: 10px;
  }

  &.disabled {
    cursor: default;
    opacity: 0.45;

    &:hover {
      border-color: rgba(255, 255, 255, 0.06);
      transform: none;
    }
  }
`;

const HeroStyled = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-gap: 20px;
  align-items: start;
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }

  .eyebrow {
    color: #36A2EF;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 7px;
    text-transform: uppercase;
  }

  .summary {
    color: rgba(255, 255, 255, 0.62);
    font-size: 13px;
    line-height: 1.55;
    margin-top: 8px;
    max-width: 680px;
  }
`;

const QuotaGridStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const QuotaItemStyled = styled.div`
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.055);
  border-radius: 6px;
  min-width: 0;
  padding: 10px 12px;

  .label {
    color: rgba(255, 255, 255, 0.48);
    display: block;
    font-size: 11px;
    margin-bottom: 5px;
  }

  .value {
    color: rgba(255, 255, 255, 0.9);
    display: block;
    font-size: 13px;
    font-weight: 700;
  }
`;

const FormWrapStyled = styled.div`
  margin-top: 18px;
`;

const monitorOptions = {
    [MONITOR_TYPES.SERVER]: {
        description: 'Linux agent metrics, processes, disks, network, and alarms.',
        icon: 'compute',
        label: 'Server agent'
    },
    [MONITOR_TYPES.HEARTBEAT]: {
        description: 'HTTP or ping checks for public endpoints and internal hosts.',
        icon: 'uptimeApp',
        label: 'Uptime check'
    },
    [MONITOR_TYPES.MONGO]: {
        description: 'MongoDB stats, storage, connections, operations, and databases.',
        icon: 'logoMongodb',
        label: 'MongoDB'
    }
};

const CreateServerObserved = ({
    canAddNewServers,
    canAddNewUptime,
    canAddNewMongoDb,
    serverCreated
}) => {
    const [loader, setLoader] = useState(true);
    const [isTagsModalOpen, setIsTagsModalOpen] = useState(false);
    const [typeOfMonitor, setTypeOfMonitor] = useState(MONITOR_TYPES.SERVER);

    useEffect(() => {
        let active = true;
        const getsData = async () => {
            await Promise.all([
                serverStore.getServers(),
                tagsStore.getTags(),
                uptimeStore.getUptime(),
                mongoDbStore.getMongoDb()
            ]);
        };

        getsData()
            .catch(error => addToast(error.message || 'Failed to load monitors', 'error'))
            .finally(() => {
                if (active) {
                    setLoader(false);
                }
            });
        return () => {
            active = false;
        };
    }, []);

    const canCreateMap = useMemo(() => ({
        [MONITOR_TYPES.SERVER]: canAddNewServers,
        [MONITOR_TYPES.HEARTBEAT]: canAddNewUptime,
        [MONITOR_TYPES.MONGO]: canAddNewMongoDb
    }), [canAddNewServers, canAddNewUptime, canAddNewMongoDb]);

    useEffect(() => {
        const availableType = selectAvailableMonitorType(
            canCreateMap,
            typeOfMonitor,
            serverCreated
        );
        if (availableType && availableType !== typeOfMonitor) {
            setTypeOfMonitor(availableType);
        }
    }, [canCreateMap, serverCreated, typeOfMonitor]);

    const canShowSelectedForm = canShowMonitorForm(
        canCreateMap,
        typeOfMonitor,
        serverCreated
    );

    return (
        <Styles>
            <Header/>
            { loader && (
                <LoaderComponent/>
            ) }

            { !loader && (
                <ChooseMonitorWrapStyled>
                    <HeroStyled>
                        <div>
                            <div className="eyebrow">Create monitor</div>
                            <h1>Add a new monitor</h1>
                            <div className="summary">
                                Choose the kind of signal you want SyAgent to collect, then complete the setup details below.
                            </div>
                        </div>
                        <QuotaGridStyled>
                            <QuotaItemStyled>
                                <span className="label">Server slots</span>
                                <span className="value">{ canAddNewServers ? 'Available' : 'Limit reached' }</span>
                            </QuotaItemStyled>
                            <QuotaItemStyled>
                                <span className="label">Uptime slots</span>
                                <span className="value">{ canAddNewUptime ? 'Available' : 'Limit reached' }</span>
                            </QuotaItemStyled>
                            <QuotaItemStyled>
                                <span className="label">Mongo slots</span>
                                <span className="value">{ canAddNewMongoDb ? 'Available' : 'Limit reached' }</span>
                            </QuotaItemStyled>
                        </QuotaGridStyled>
                    </HeroStyled>

                <ChooseMonitorStyled>
                    { Object.values(MONITOR_TYPES).map((type, idx) => {
                        const isDisabled = !canCreateMap[type];
                        const option = monitorOptions[type];
                        const isActive = type === typeOfMonitor;
                        let styles = `oir_${ idx }`;

                        if (isDisabled) {
                            styles += ' disabled';
                        }
                        return (
                            <ChooseMonitorItemStyled
                                active={ isActive }
                                disabled={ isDisabled }
                                onClick={ () => {
                                    if (isDisabled) {
                                        return;
                                    }
                                    setTypeOfMonitor(type);
                                } }
                                className={ styles }
                                key={ `dkkkkz_${ idx }` }
                            >
                                <span className="icon-wrapper">
                                    <EuiIcon type={ option.icon } size="m"/>
                                </span>
                                <span>
                                    <span className="title">{ option.label }</span>
                                    <span className="description">{ option.description }</span>
                                    <span className="status">{ isDisabled ? 'Plan limit reached' : 'Ready to create' }</span>
                                </span>
                            </ChooseMonitorItemStyled>
                        );
                    }) }
                </ChooseMonitorStyled>

                <FormWrapStyled>
                    { canShowSelectedForm && typeOfMonitor === MONITOR_TYPES.SERVER && (
                        <CreateNewServerComponent
                            setIsTagsModalOpen={ setIsTagsModalOpen }/>
                    ) }

                    { canShowSelectedForm && typeOfMonitor === MONITOR_TYPES.HEARTBEAT && (
                        <CreateNewMonitorComponent
                            setIsTagsModalOpen={ setIsTagsModalOpen }/>
                    ) }

                    { canShowSelectedForm && typeOfMonitor === MONITOR_TYPES.MONGO && (
                        <CreateNewMongoMonitorComponent
                            setIsTagsModalOpen={ setIsTagsModalOpen }
                        />
                    ) }
                </FormWrapStyled>
                </ChooseMonitorWrapStyled>
            ) }

            { isTagsModalOpen && (
                <TagsModal close={ () => setIsTagsModalOpen(false) }/>
            ) }
        </Styles>
    );
};

export default CreateServerObserved;
